/**
 * electric claimable claim <claimId>
 *
 * Full claim flow:
 * 1. Check claimable source is ready and has a claim_link (dashboard URL)
 * 2. Start local HTTP server for browser callback
 * 3. Append cli_port to claim_link so the dashboard passes it through the Neon flow
 * 4. Open browser for ownership transfer
 * 5. Wait for callback confirming transfer completed
 * 6. Call the claim API to move the source into the user's workspace
 */

import { Command } from 'commander'
import { URL } from 'node:url'
import {
  createClient,
  createPublicClient,
  getDashboardUrl,
} from '../../client.js'
import { handleError, exitWithError, EXIT_CONFLICT } from '../../lib/errors.js'
import { resolveWorkspace } from '../../lib/workspace-resolver.js'
import { startLocalServer } from '../../lib/local-server.js'
import {
  isQuiet,
  printQuiet,
  printJson,
  printSuccess,
  printKeyValue,
} from '../../output.js'

const CLAIM_TIMEOUT_MS = 10 * 60 * 1000 // 10 minutes

interface ClaimCallbackResult {
  claimId: string
  claimed: boolean
}

export const claimCommand = new Command(`claim`)
  .description(`Claim a provisioned service into your workspace`)
  .argument(`<claimId>`, `Claim ID`)
  .option(`--workspace <id>`, `Workspace ID`)
  .option(
    `--environment <id>`,
    `Environment ID (optional — auto-created if omitted)`
  )
  .action(async (claimId: string, opts) => {
    const json = claimCommand.parent?.parent?.opts().json ?? false
    try {
      await runClaim(claimId, opts, json)
    } catch (error) {
      handleError(error, json)
    }
  })

async function runClaim(
  claimId: string,
  opts: { workspace?: string; environment?: string },
  json: boolean
): Promise<void> {
  const publicClient = createPublicClient()

  // 1. Validate the claimable source is ready
  const status = await publicClient.claimable.get({ claimId })

  if (status.state !== `ready`) {
    exitWithError(
      `Claimable service is not ready (state: ${status.state}).`,
      EXIT_CONFLICT,
      json
    )
  }

  if (!status.claim_link) {
    exitWithError(
      `Claimable service has no claim link. It may have already been claimed.`,
      EXIT_CONFLICT,
      json
    )
  }

  // 2. Start local server for browser callback
  const { port, resultPromise, shutdown } =
    await startLocalServer<ClaimCallbackResult>({
      callbackPath: `/callback`,
      parseParams: (params) => {
        const cbClaimId = params.get(`claimId`)
        const claimed = params.get(`claimed`)
        if (!cbClaimId) {
          throw new Error(`Missing claimId in callback`)
        }
        return { claimId: cbClaimId, claimed: claimed === `true` }
      },
      successHtml: `<html><body><p>Ownership transfer complete. You can close this tab.</p><script>window.close()</script></body></html>`,
      errorHtml: `<html><body><p>Something went wrong during the claim. Please try again.</p></body></html>`,
    })

  // 3. Build the dashboard claim URL with cli_port so the dashboard passes it
  //    through the Neon transfer flow and back to the claimed-callback endpoint.
  const claimUrl = new URL(`/claim`, getDashboardUrl())
  claimUrl.searchParams.set(`uuid`, claimId)
  claimUrl.searchParams.set(`cli_port`, String(port))

  // 4. Open browser
  const browserUrl = claimUrl.toString()
  try {
    const open = (await import(`open`)).default
    await open(browserUrl)
    if (!json) {
      process.stderr.write(
        `Opening browser for Neon ownership transfer...\nIf the browser doesn't open, visit:\n${browserUrl}\n`
      )
    }
  } catch {
    process.stderr.write(
      `Could not open browser. Visit this URL to complete the transfer:\n${browserUrl}\n`
    )
  }

  // 5. Wait for callback or timeout
  let timeoutId: ReturnType<typeof setTimeout>
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      shutdown()
      reject(
        new Error(
          `Claim timed out after ${CLAIM_TIMEOUT_MS / 60_000} minutes. Run 'electric claimable claim ${claimId}' to try again.`
        )
      )
    }, CLAIM_TIMEOUT_MS)
  })

  let callbackResult: ClaimCallbackResult
  try {
    callbackResult = await Promise.race([resultPromise, timeoutPromise])
    clearTimeout(timeoutId!)
    shutdown()
  } catch (error) {
    shutdown()
    throw error
  }

  if (!callbackResult.claimed) {
    exitWithError(
      `Ownership transfer was not completed. Run 'electric claimable claim ${claimId}' to try again.`,
      EXIT_CONFLICT,
      json
    )
  }

  // 6. Call the claim API (requires auth)
  if (!json) {
    process.stderr.write(`Ownership transfer confirmed. Claiming service...\n`)
  }

  const client = createClient()
  const workspaceId = await resolveWorkspace(client, opts.workspace)

  const result = await client.claimable.claim({
    claimId,
    workspaceId,
    ...(opts.environment ? { environmentId: opts.environment } : {}),
  })

  if (isQuiet()) {
    printQuiet(result.serviceId)
    return
  }

  if (json) {
    printJson(result)
  } else {
    printSuccess(`Source claimed successfully.\n`)
    printKeyValue([
      [`Service ID:`, result.serviceId],
      [`Service type:`, result.serviceType],
      [`Project ID:`, result.projectId],
      [`Environment ID:`, result.environmentId],
    ])
  }
}
